import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Save,
  Trash2,
  Clapperboard,
  Play,
  Lightbulb,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Minus,
  ArrowUpRight,
  Info,
  BarChart2,
} from "lucide-react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader } from "@/components/states";
import { useSubscription } from "@/context/SubscriptionContext";
import { useConfirm } from "@/context/ConfirmContext";

const PLATFORMS = ["youtube", "instagram"];


const number = (value) => (value == null ? "—" : Number(value).toLocaleString());
const signed = (value) => {
  if (value == null) return "—";
  const n = Number(value);
  const formatted = Math.abs(n).toLocaleString();
  if (n > 0) return `+${formatted}`;
  if (n < 0) return `-${formatted}`;
  return formatted;
};

export default function ContentEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const [content, setContent] = useState(null);
  const [learnings, setLearnings] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", platforms: [], status: "draft" });
  const [state, setState] = useState({ loading: true, saving: false, deleting: false, error: null, saved: false });

  useEffect(() => {
    let active = true;
    Promise.all([
      api.get(`/content/${id}`),
      api.get(`/analytics/content/${id}/learnings`).catch(() => ({ data: null })),
    ])
      .then(([{ data: contentData }, { data: learningsData }]) => {
        if (!active) return;
        setContent(contentData);
        setLearnings(learningsData);
        setForm({
          title: contentData.title || "",
          body: contentData.body || "",
          platforms: contentData.platforms || [],
          status: contentData.status || "draft",
        });
        setState((current) => ({ ...current, loading: false }));
      })
      .catch((error) => {
        if (active) setState((current) => ({ ...current, loading: false, error: apiErrorMessage(error) }));
      });
    return () => {
      active = false;
    };
  }, [id]);

  const { subscription, isTrial, isAdmin, openPaywall, refreshSubscription } = useSubscription();
  const [refiningScript, setRefiningScript] = useState(false);

  const analysisLimit = subscription?.quotas?.analysis || (isTrial ? 5 : 0);
  const analysisUsed = subscription?.usage?.analysis || 0;
  const analysisRemaining = Math.max(0, analysisLimit - analysisUsed);
  const canRefine = isAdmin || analysisRemaining > 0;

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setState((current) => ({ ...current, saved: false, error: null }));
  };

  const togglePlatform = (platform) =>
    update(
      "platforms",
      form.platforms.includes(platform)
        ? form.platforms.filter((item) => item !== platform)
        : [...form.platforms, platform]
    );

  const refineScript = async () => {
    if (!canRefine) {
      openPaywall("Script Refinement & AI Analysis");
      return;
    }

    const charCount = (form.body || "").trim().length;
    if (charCount < 100) {
      toast.error(`Please write at least 100 characters in your draft before refining (currently ${charCount} chars).`);
      document.getElementById("content-body")?.focus();
      return;
    }

    setRefiningScript(true);
    try {
      const { data } = await api.post(`/content/${id}/refine-script`, {
        title: form.title.trim(),
        body: form.body,
        platforms: form.platforms,
      });

      if (data.script) {
        update("body", data.script);
        const count = data.improvements_count || 0;
        toast.success(
          count > 0
            ? `Script refined! Applied ${count} AI recommendations & improvements.`
            : "Script refined and polished with AI analysis!"
        );
        refreshSubscription();
      }
    } catch (error) {
      if (error.response?.status === 402) {
        openPaywall("Script Refinement & AI Analysis");
      }
      toast.error(apiErrorMessage(error));
    } finally {
      setRefiningScript(false);
    }
  };


  const save = async () => {
    setState((current) => ({ ...current, saving: true, error: null, saved: false }));
    try {
      const { data } = await api.put(`/content/${id}`, form);
      setContent(data);
      setForm({
        title: data.title || "",
        body: data.body || "",
        platforms: data.platforms || [],
        status: data.status || "draft",
      });
      setState((current) => ({ ...current, saving: false, saved: true }));
      toast.success("Content saved");
    } catch (error) {
      setState((current) => ({ ...current, saving: false, error: apiErrorMessage(error) }));
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: "Delete this content draft?",
      description: "This draft will be permanently deleted and cannot be recovered.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    setState((current) => ({ ...current, deleting: true, error: null }));
    try {
      await api.delete(`/content/${id}`);
      toast.success("Content deleted");
      navigate("/content");
    } catch (error) {
      setState((current) => ({ ...current, deleting: false, error: apiErrorMessage(error) }));
    }
  };

  const startProduction = async () => {
    try {
      const { data: project } = await api.post(`/production`, { content_id: id });
      await api.patch(`/content/${id}/production`, { production_status: "in_production" });
      toast.success("Production started");
      navigate(`/production/${project.id}`);
    } catch (error) {
      toast.error(apiErrorMessage(error));
    }
  };

  if (state.loading) return <AppShell title="Content Editor"><Loader label="Loading draft" /></AppShell>;
  if (state.error && !content)
    return (
      <AppShell title="Content Editor">
        <div className="space-y-4">
          <ErrorState message={state.error} onRetry={load} />
          <div className="flex justify-center">
            <Button variant="outline" onClick={() => navigate("/content")} data-testid="content-editor-error-back">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Content Library
            </Button>
          </div>
        </div>
      </AppShell>
    );

  return (
      <AppShell title="Content Editor">
      <div className="mb-6 border-b border-border pb-5">
        {/* Row 1: back + actions */}
        <div className="flex flex-col xs:flex-row xs:items-center justify-between gap-3">
          <Button variant="ghost" onClick={() => navigate("/content")} data-testid="content-editor-back-button" className="shrink-0 self-start xs:self-auto h-8 sm:h-9 text-xs sm:text-sm px-2 sm:px-3">
            <ArrowLeft className="mr-1.5 sm:mr-2 h-4 w-4" /> <span>Content library</span>
          </Button>
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 justify-end w-full xs:w-auto">
            {state.saved && <span className="text-xs text-accent font-medium mr-1" data-testid="content-saved-state">Saved</span>}
            {content && (content.production_status || "draft") === "draft" ? (
              <Button variant="outline" size="sm" onClick={startProduction} data-testid="content-start-production" className="h-8 text-xs">
                <Play className="mr-1.5 h-3.5 w-3.5" /> <span className="hidden sm:inline">Start Production</span><span className="sm:hidden">Produce</span>
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/production`, { content_id: id });
                    navigate(`/production/${data.id}`);
                  } catch (e) {
                    toast.error(apiErrorMessage(e));
                  }
                }}
                data-testid="content-view-production"
                className="h-8 text-xs"
              >
                <Clapperboard className="mr-1.5 h-3.5 w-3.5" /> <span className="hidden sm:inline">View Production</span><span className="sm:hidden">Production</span>
              </Button>
            )}
            <Button
              size="sm"
              onClick={save}
              disabled={state.saving || !form.title.trim()}
              data-testid="content-save-button"
              className="h-8 text-xs bg-accent text-accent-foreground hover:bg-accent-hover shadow-sm"
            >
              <Save className="mr-1.5 h-3.5 w-3.5" />
              {state.saving ? "Saving…" : "Save"}
            </Button>
            <Button size="sm" variant="outline" onClick={remove} disabled={state.deleting} data-testid="content-delete-button" className="h-8 text-xs">
              <Trash2 className="mr-1.5 h-3.5 w-3.5 text-destructive" /> <span className="hidden sm:inline">Delete</span>
            </Button>
          </div>
        </div>
      </div>

      {state.error && (
        <div className="mb-5 rounded-md border border-destructive/40 bg-surface p-3 text-sm text-destructive" data-testid="content-editor-error">
          {state.error}
        </div>
      )}

      {/* Creation loop connection: Learn from past content */}
      <div
        className="mb-6 flex flex-col gap-3 rounded-lg border border-accent/20 bg-accent/5 p-4 sm:flex-row sm:items-center sm:justify-between"
        data-testid="content-learning-banner"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent/20 text-accent">
            <Lightbulb className="h-4 w-4" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-accent">Learn from your past content</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Review your channel's real view and engagement trends to write stronger hooks and topics.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" asChild className="shrink-0 text-xs self-start sm:self-auto">
          <Link to="/analytics" data-testid="content-view-analytics-link">
            <BarChart2 className="mr-1.5 h-3.5 w-3.5" /> View Analytics
          </Link>
        </Button>
      </div>

      <div className="grid gap-6 md:gap-8 md:grid-cols-[minmax(0,1fr)_280px] lg:grid-cols-[minmax(0,1fr)_300px]">
        {/* Main Content Area */}
        <section className="space-y-6 min-w-0">
          <div>
            <label htmlFor="content-title" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Title
            </label>
            <Input
              id="content-title"
              value={form.title}
              onChange={(event) => update("title", event.target.value)}
              placeholder="Give this piece a clear working title"
              data-testid="content-title-input"
            />
          </div>
          <div>
            <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <label htmlFor="content-body" className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground shrink-0">
                  Working content
                </label>
                <span
                  className={`truncate rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors ${
                    (form.body || "").trim().length >= 100
                      ? "bg-accent/15 text-accent"
                      : "bg-surface text-muted-foreground border border-border"
                  }`}
                  data-testid="content-char-badge"
                >
                  {(form.body || "").trim().length}/100 chars min
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2 justify-between sm:justify-end w-full sm:w-auto">
                <span className="text-xs text-muted-foreground" data-testid="content-word-count">
                  {(form.body || "").trim().split(/\s+/).filter(Boolean).length} words
                </span>
                <span className="text-[11px] text-muted-foreground">
                  · Quota: {isAdmin ? "Unlimited" : `${analysisRemaining} left`}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={refineScript}
                  disabled={refiningScript || (form.body || "").trim().length < 100}
                  data-testid="content-refine-script-button"
                  title={(form.body || "").trim().length < 100 ? "Write at least 100 characters in your draft to refine script" : "Analyze and refine script with AI"}
                  className={`h-7 px-3 text-xs font-semibold shadow-sm transition-all ${
                    (form.body || "").trim().length >= 100
                      ? "border-accent/40 bg-accent/10 text-accent hover:bg-accent hover:text-accent-foreground"
                      : "border-border bg-surface text-muted-foreground opacity-60 cursor-not-allowed"
                  }`}
                >
                  <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                  {refiningScript ? "Refining…" : "Refine Script"}
                </Button>
              </div>
            </div>
            <textarea
              id="content-body"
              value={form.body}
              onChange={(event) => update("body", event.target.value)}
              placeholder="Write your draft script here (at least 100 characters). Then click 'Refine Script' to analyze weaknesses with AI and produce a clean, polished, high-retention script without markdown asterisks…"
              className="min-h-[320px] sm:min-h-[420px] w-full resize-y rounded-md border border-border bg-surface p-3.5 sm:p-4 text-xs sm:text-sm leading-6 sm:leading-7 text-foreground font-mono outline-none transition-colors placeholder:text-muted-foreground focus:border-accent focus:ring-2 focus:ring-accent/20"
              data-testid="content-body-input"
            />
          </div>

          {/* Per-content Learning Section: "What can you learn from this?" */}
          <section className="rounded-xl border border-border bg-surface p-4 sm:p-6 space-y-4 min-w-0" data-testid="content-learnings-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded bg-accent/15 text-accent">
                  <Sparkles className="h-4 w-4" />
                </div>
                <h3 className="font-display text-base font-semibold text-foreground" data-testid="content-learnings-title">
                  What can you learn from this?
                </h3>
              </div>
              {learnings?.available && (
                <span className="text-xs text-muted-foreground">
                  vs {learnings.baseline?.count || 0} baseline video{learnings.baseline?.count === 1 ? "" : "s"}
                </span>
              )}
            </div>

            {learnings?.available ? (
              <div className="space-y-4" data-testid="content-learnings-available">
                {/* Metric Delta Cards */}
                <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
                  {/* Views vs baseline */}
                  <div className="rounded-lg border border-border bg-elevated/40 p-3.5">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Views</span>
                    <p className="mt-1 font-display text-lg font-bold text-foreground">
                      {number(learnings.metrics?.views)}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {learnings.deltas?.views_pct != null ? (
                        <span className={learnings.directions?.views === "above" ? "text-accent font-medium" : learnings.directions?.views === "below" ? "text-destructive font-medium" : ""}>
                          {learnings.deltas.views_pct > 0 ? `+${learnings.deltas.views_pct}%` : `${learnings.deltas.views_pct}%`} vs baseline
                        </span>
                      ) : (
                        `Baseline: ${number(learnings.baseline?.avg_views)}`
                      )}
                    </p>
                  </div>

                  {/* Engagement vs baseline */}
                  <div className="rounded-lg border border-border bg-elevated/40 p-3.5">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Engagement</span>
                    <p className="mt-1 font-display text-lg font-bold text-foreground">
                      {learnings.metrics?.engagement_rate != null ? `${learnings.metrics.engagement_rate}%` : "—"}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {learnings.directions?.engagement === "above" ? (
                        <span className="text-accent font-medium">Above recent average</span>
                      ) : learnings.directions?.engagement === "below" ? (
                        <span className="text-destructive font-medium">Below recent average</span>
                      ) : (
                        "In line with average"
                      )}
                    </p>
                  </div>

                  {/* Net subscribers vs baseline */}
                  <div className="rounded-lg border border-border bg-elevated/40 p-3.5">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Subscribers</span>
                    <p className="mt-1 font-display text-lg font-bold text-foreground">
                      {signed(learnings.metrics?.net_subscribers)}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {learnings.directions?.net_subscribers === "above" ? (
                        <span className="text-accent font-medium">Above typical content</span>
                      ) : (
                        "In line with average"
                      )}
                    </p>
                  </div>
                </div>

                {/* Specific learning takeaways */}
                {(learnings.learnings || []).length > 0 && (
                  <div className="rounded-lg border border-accent/20 bg-accent/5 p-4 space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-accent">Key Takeaways</p>
                    <ul className="space-y-1.5 text-sm text-foreground" data-testid="content-learnings-list">
                      {learnings.learnings.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2" data-testid={`content-learning-item-${idx}`}>
                          <Sparkles className="mt-1 h-3.5 w-3.5 shrink-0 text-accent" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border bg-elevated/30 p-4 text-xs text-muted-foreground" data-testid="content-learnings-empty">
                <div className="flex items-start gap-2.5">
                  <Info className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" />
                  <div>
                    {content?.platform_content_id ? (
                      <p>
                        No analytics snapshots recorded yet for YouTube video ID <span className="font-mono text-foreground font-semibold">{content.platform_content_id}</span>.
                        Sync your channel analytics from the <Link to="/analytics" className="text-accent underline underline-offset-2">Analytics page</Link> to generate learnings.
                      </p>
                    ) : (
                      <p>
                        Performance learnings are computed when this content is published to YouTube and connected to a video ID.
                        Virality will compare views, engagement, and subscriber impact against your own baseline.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>
        </section>

        {/* Sidebar */}
        <aside className="h-fit space-y-6 rounded-xl border border-border bg-surface p-4 sm:p-5 min-w-0">
          <div>
            <label htmlFor="content-status" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Status
            </label>
            <select
              id="content-status"
              value={form.status}
              onChange={(event) => update("status", event.target.value)}
              className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none focus:border-accent"
              data-testid="content-status-select"
            >
              {["draft", "recording", "analyzing", "ready", "published"].map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Platforms</p>
            <div className="space-y-2">
              {PLATFORMS.map((platform) => (
                <label
                  key={platform}
                  className="flex cursor-pointer items-center gap-3 text-sm text-foreground"
                  data-testid={`content-platform-option-${platform}`}
                >
                  <input
                    type="checkbox"
                    checked={form.platforms.includes(platform)}
                    onChange={() => togglePlatform(platform)}
                    data-testid={`content-platform-checkbox-${platform}`}
                  />
                  {platform}
                </label>
              ))}
            </div>
          </div>

          {content?.platform_content_id && (
            <div className="border-t border-border pt-4 text-xs" data-testid="content-platform-id-meta">
              <span className="font-semibold text-muted-foreground uppercase tracking-wider block mb-1">YouTube Video ID</span>
              <span className="font-mono text-foreground font-semibold">{content.platform_content_id}</span>
            </div>
          )}

          <div className="border-t border-border pt-4 text-xs text-muted-foreground" data-testid="content-editor-meta">
            <p>{content?.idea_id ? "Started from an idea" : "Blank draft"}</p>
            <p className="mt-1">Last updated {content?.updated_at ? new Date(content.updated_at).toLocaleString() : "not yet"}</p>
          </div>
        </aside>
      </div>
    </AppShell>
  );
}